<script setup>
const { t } = useI18n()
const actionKeys = { create_project: 'macros.actionCreateProject', add_bundle_to_project: 'macros.actionAddBundle', checkout_bundle_to_user: 'macros.actionCheckoutBundleUser', checkout_bundle_to_project: 'macros.actionCheckoutBundleProject', create_item_checkout: 'macros.actionCreateCheckout', update_project_status: 'macros.actionUpdateProjectStatus' }
const actionLabel = value => actionKeys[value] ? t(actionKeys[value]) : value
import { useI18n } from 'vue-i18n'

import { ref, computed, watch, onMounted } from 'vue'
import MdiPlay from '~icons/mdi/play'
import MdiCheck from '~icons/mdi/check'
import MdiAlert from '~icons/mdi/alert'
import MdiClose from '~icons/mdi/close'
import MdiLock from '~icons/mdi/lock'
import {
  runMacro,
  getMacroRun,
  getProjects,
  getUsers,
  getBundles,
  getItemMetadataExtended,
  getLocations,
  getWarehouse,
} from '@/api/api.js'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'

const props = defineProps({
  show: { type: Boolean, required: true },
  macro: { type: Object, default: null },
  // Maps input type → pre-filled ID. The first input whose type matches gets
  // locked (read-only) and pre-filled. E.g. { project: 42 }
  prefillByType: { type: Object, default: () => ({}) },
})
const emit = defineEmits(['close'])

// --- form state ---
const fieldValues = ref({})
const running = ref(false)
const runResult = ref(null) // MacroRun object
const pollTimer = ref(null)

// --- entity option caches ---
const entityOptions = ref({
  project: [],
  user: [],
  bundle: [],
  item_metadata: [],
  location: [],
  warehouse: [],
})
const entityLoading = ref({})
const entitySearch = ref({})
const entityDropdownOpen = ref({})
const entityDropdownPlacement = ref({})

const inputs = computed(() => {
  if (!props.macro) return []
  const raw = props.macro.inputs
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object') return Object.values(raw)
  return []
})

const allFilled = computed(() =>
  inputs.value.every((inp) => {
    const v = fieldValues.value[inp.name]
    return v !== null && v !== undefined && v !== ''
  })
)

const isDone = computed(() =>
  runResult.value && ['success', 'failed', 'rolled_back'].includes(runResult.value.status)
)

const isPolling = computed(() =>
  runResult.value && ['pending', 'running'].includes(runResult.value.status)
)

function extractSkippedItems(run) {
  if (!run) return []
  const items = []

  const topLevel = run.result?.skipped_items
  if (Array.isArray(topLevel)) {
    for (const s of topLevel) {
      items.push({
        stepLabel: null,
        asset_tag: s?.asset_tag ?? t('common.unknown'),
        reason: s?.reason ?? t('ui.noReasonProvided'),
      })
    }
  }

  const steps = Array.isArray(run.step_runs) ? run.step_runs : []
  for (const step of steps) {
    const skipped = step?.result?.skipped_items
    if (!Array.isArray(skipped) || !skipped.length) continue
    for (const s of skipped) {
      items.push({
        stepLabel: step?.label || actionLabel(step?.action_type) || t('ui.stepNumber', { id: Number(step?.order ?? 0) + 1 }),
        asset_tag: s?.asset_tag ?? t('common.unknown'),
        reason: s?.reason ?? t('ui.noReasonProvided'),
      })
    }
  }

  return items
}

const skippedItems = computed(() => extractSkippedItems(runResult.value))

const isWarningSuccess = computed(() =>
  runResult.value?.status === 'success' && skippedItems.value.length > 0
)

function formatDateForInput(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseRelativeNowToken(value) {
  if (typeof value !== 'string') return null
  const raw = value.trim().toLowerCase()
  if (!raw) return null
  if (raw === 'now') return new Date()

  const m = raw.match(/^now([+-])(\d+)([dwh])$/)
  if (!m) return null

  const sign = m[1] === '+' ? 1 : -1
  const amount = Number(m[2])
  const unit = m[3]
  const dt = new Date()

  if (unit === 'd') {
    dt.setDate(dt.getDate() + sign * amount)
    return dt
  }
  if (unit === 'w') {
    dt.setDate(dt.getDate() + sign * amount * 7)
    return dt
  }
  if (unit === 'h') {
    dt.setHours(dt.getHours() + sign * amount)
    return dt
  }

  return null
}

function resolveInputDefaultValue(inp) {
  const def = inp.default ?? ''
  if (inp.type !== 'date') return def

  const parsedRelative = parseRelativeNowToken(def)
  if (parsedRelative) return formatDateForInput(parsedRelative)

  // Also normalize ISO-like defaults to YYYY-MM-DD for native date inputs.
  if (typeof def === 'string' && def.trim()) {
    const dt = new Date(def)
    if (!Number.isNaN(dt.getTime())) return formatDateForInput(dt)
  }

  return def
}

// Pre-fill defaults when macro changes
watch(
  () => props.macro,
  (m) => {
    fieldValues.value = {}
    runResult.value = null
    entityDropdownOpen.value = {}
    entitySearch.value = {}
    if (!m) return
    const ins = Array.isArray(m.inputs) ? m.inputs : Object.values(m.inputs ?? {})
    // Track which types have already been claimed by prefillByType so only the
    // first matching input per type gets locked.
    const claimedTypes = new Set()
    for (const inp of ins) {
      const prefillId = props.prefillByType[inp.type]
      if (prefillId !== undefined && !claimedTypes.has(inp.type)) {
        fieldValues.value[inp.name] = prefillId
        claimedTypes.add(inp.type)
      } else {
        fieldValues.value[inp.name] = resolveInputDefaultValue(inp)
      }
    }
    preloadEntityOptions(ins)
  },
  { immediate: true }
)

// Set of input names that are locked (pre-filled from page context).
const lockedInputNames = computed(() => {
  if (!props.macro) return new Set()
  const ins = Array.isArray(props.macro.inputs)
    ? props.macro.inputs
    : Object.values(props.macro.inputs ?? {})
  const locked = new Set()
  const claimedTypes = new Set()
  for (const inp of ins) {
    const prefillId = props.prefillByType[inp.type]
    if (prefillId !== undefined && !claimedTypes.has(inp.type)) {
      locked.add(inp.name)
      claimedTypes.add(inp.type)
    }
  }
  return locked
})

async function preloadEntityOptions(ins) {
  const typesNeeded = [...new Set(ins.map((i) => i.type).filter((t) => isEntityType(t)))]
  for (const t of typesNeeded) {
    await loadEntityOptions(t)
  }
}

function isEntityType(type) {
  return ['project', 'user', 'bundle', 'item_metadata', 'location', 'warehouse'].includes(type)
}

const ENTITY_LOADERS = {
  project: () => getProjects({ page_size: 200 }),
  user: () => getUsers({ page_size: 200 }),
  bundle: () => getBundles({ page_size: 200 }),
  item_metadata: () => getItemMetadataExtended(0, 0, 0, { page_size: 200 }),
  location: () => getLocations({ page_size: 200 }),
  warehouse: () => getWarehouse(0, 0),
}

function entityLabel(type, item) {
  if (type === 'project') return item.title ?? item.name ?? `#${item.id}`
  if (type === 'user') return item.email ?? item.username ?? `#${item.id}`
  if (type === 'bundle') return item.name ?? `#${item.id}`
  if (type === 'item_metadata') return item.asset_tag ?? item.serial_number ?? item.item?.name ?? `#${item.id}`
  if (type === 'location') return item.name ?? `#${item.id}`
  if (type === 'warehouse') return item.name ?? `#${item.id}`
  return String(item.id)
}

async function loadEntityOptions(type) {
  if (entityLoading.value[type] || entityOptions.value[type].length) return
  entityLoading.value = { ...entityLoading.value, [type]: true }
  try {
    const res = await ENTITY_LOADERS[type]()
    entityOptions.value = {
      ...entityOptions.value,
      [type]: (res.results ?? res ?? []).map((item) => ({
        id: item.id,
        label: entityLabel(type, item),
      })),
    }
  } catch {
    entityOptions.value = { ...entityOptions.value, [type]: [] }
  } finally {
    entityLoading.value = { ...entityLoading.value, [type]: false }
  }
}

function filteredOptions(type) {
  const q = (entitySearch.value[type] ?? '').toLowerCase()
  return entityOptions.value[type].filter((o) => !q || o.label.toLowerCase().includes(q))
}

function selectEntity(inp, id) {
  fieldValues.value[inp.name] = id
  entityDropdownOpen.value = { ...entityDropdownOpen.value, [inp.name]: false }
  entitySearch.value = { ...entitySearch.value, [inp.name]: '' }
}

function entityDisplayLabel(inp) {
  const type = inp.type
  const id = fieldValues.value[inp.name]
  if (!id) return ''
  const opt = entityOptions.value[type]?.find((o) => o.id == id)
  return opt ? opt.label : `#${id}`
}

function toggleDropdown(inp, event) {
  const name = inp.name
  const isOpen = entityDropdownOpen.value[name]
  entityDropdownOpen.value = {}
  if (!isOpen) {
    const rect = event?.currentTarget?.getBoundingClientRect?.()
    if (rect) {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      const dropdownHeight = 260
      const spaceBelow = viewportHeight - rect.bottom
      entityDropdownPlacement.value = {
        ...entityDropdownPlacement.value,
        [name]: spaceBelow < dropdownHeight ? 'up' : 'down',
      }
    }
    entityDropdownOpen.value = { [name]: true }
    loadEntityOptions(inp.type)
  }
}

// --- run + poll ---
async function submit() {
  if (!allFilled.value || running.value) return
  running.value = true
  runResult.value = null
  try {
    const result = await runMacro(props.macro.id, fieldValues.value)
    runResult.value = result
    if (!isDone.value) {
      schedulePoll(result.id)
    }
  } catch (e) {
    runResult.value = {
      status: 'failed',
      error_message: e?.data?.detail ?? e?.message ?? t('ui.failedToStartMacro'),
      step_runs: [],
    }
  } finally {
    running.value = false
  }
}

function schedulePoll(runId) {
  pollTimer.value = setTimeout(async () => {
    try {
      const res = await getMacroRun(runId)
      runResult.value = res
      if (!isDone.value) {
        schedulePoll(runId)
      }
    } catch {
      // keep polling on transient errors
      schedulePoll(runId)
    }
  }, 1500)
}

function close() {
  clearTimeout(pollTimer.value)
  runResult.value = null
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show && macro"
      class="modal-overlay"
      @click.self="close"
    >
      <div class="confirm-modal" style="max-width: 520px; width: 95vw;">

        <!-- Header -->
        <div class="flex items-start justify-between gap-3 p-5 border-b border-[var(--color-border)]">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <MdiPlay class="w-4 h-4 text-[var(--color-primary)] flex-shrink-0" />
              <h2 class="text-base font-semibold text-[var(--color-text)] m-0 truncate">{{ macro.name }}</h2>
            </div>
            <p v-if="macro.description" class="text-sm text-[var(--color-muted)] m-0">{{ macro.description }}</p>
          </div>
          <button class="btn-icon flex-shrink-0" @click="close">
            <MdiClose class="w-4 h-4" />
          </button>
        </div>

        <!-- Run result: polling -->
        <div v-if="isPolling" class="p-6 flex flex-col items-center gap-3 text-center">
          <svg class="w-8 h-8 text-[var(--color-primary)] animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p class="text-sm text-[var(--color-muted)] m-0">{{ $t('macros.running') }}</p>
          <p class="text-xs text-[var(--color-muted)] m-0 capitalize">{{ runResult.status }}</p>
        </div>

        <!-- Run result: done -->
        <div v-else-if="isDone" class="p-5 flex flex-col gap-4">
          <!-- Overall status -->
          <div
            :class="[
              'flex items-center gap-3 p-4 rounded-lg',
              isWarningSuccess
                ? 'bg-amber-950/40 border border-amber-700/40'
                : runResult.status === 'success'
                  ? 'bg-emerald-950/40 border border-emerald-700/30'
                  : 'bg-red-950/40 border border-red-700/30'
            ]"
          >
            <MdiCheck v-if="runResult.status === 'success' && !isWarningSuccess" class="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <MdiAlert v-else :class="['w-5 h-5 flex-shrink-0', isWarningSuccess ? 'text-amber-400' : 'text-red-400']" />
            <div>
              <div
                :class="[
                  'font-semibold text-sm',
                  isWarningSuccess
                    ? 'text-amber-300'
                    : runResult.status === 'success'
                      ? 'text-emerald-300'
                      : 'text-red-300'
                ]"
              >
                {{ isWarningSuccess ? $t('macros.runWarning') : runResult.status === 'success' ? $t('macros.runSuccess') : $t('macros.runFailed') }}
              </div>
              <div v-if="runResult.error_message" class="text-xs text-red-400 mt-0.5">{{ runResult.error_message }}</div>
            </div>
          </div>

          <div v-if="isWarningSuccess && skippedItems.length" class="flex flex-col gap-2">
            <div class="section-label text-amber-300">{{ $t('macros.skippedHeader') }}</div>
            <div
              v-for="(item, idx) in skippedItems"
              :key="`${item.asset_tag}-${idx}`"
              class="p-3 rounded-lg bg-amber-950/20 border border-amber-700/30"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="text-sm font-semibold text-amber-200">{{ item.asset_tag }}</div>
                <span v-if="item.stepLabel" class="badge badge-warning text-[10px]">{{ item.stepLabel }}</span>
              </div>
              <div class="text-xs text-amber-100/90 mt-1">{{ item.reason }}</div>
            </div>
          </div>

          <!-- Step results -->
          <div v-if="runResult.step_runs?.length" class="flex flex-col gap-2">
            <div class="section-label">{{ $t('macros.stepsSection') }}</div>
            <div
              v-for="step in runResult.step_runs"
              :key="step.id"
              class="flex items-start gap-3 p-3 rounded-lg bg-[var(--color-surface-2)] border border-[var(--color-border)]"
            >
              <span
                :class="[
                  'flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold',
                  step.status === 'success' ? 'bg-emerald-800 text-emerald-200' :
                  step.status === 'failed' ? 'bg-red-800 text-red-200' :
                  'bg-gray-700 text-gray-400'
                ]"
              >{{ step.order + 1 }}</span>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-[var(--color-text)]">{{ step.label || actionLabel(step.action_type) }}</div>
                <div v-if="step.error_message" class="text-xs text-red-400 mt-0.5">{{ step.error_message }}</div>
                <div v-else-if="step.status === 'success'" class="text-xs text-emerald-500 mt-0.5">{{ $t('macros.stepCompleted') }}</div>
              </div>
              <span
                :class="[
                  'badge flex-shrink-0',
                  step.status === 'success' ? 'badge-success' :
                  step.status === 'failed' ? 'badge-danger' :
                  step.status === 'skipped' ? 'badge-muted' :
                  'badge-warning'
                ]"
              >{{ step.status }}</span>
            </div>
          </div>

          <div class="flex justify-end gap-2 pt-1">
            <button class="btn primary" @click="close">{{ $t('common.done') }}</button>
          </div>
        </div>

        <!-- Input form -->
        <div v-else class="p-5 flex flex-col gap-4">
          <div v-if="inputs.length === 0" class="text-sm text-[var(--color-muted)] text-center py-2">
            {{ $t('macros.noInputsRun') }}
          </div>
          <template v-else>
            <BaseFormField
              v-for="inp in inputs"
              :key="inp.name"
              :label="inp.label || inp.name"
              required
            >
              <!-- locked (pre-filled from page context) -->
              <div
                v-if="lockedInputNames.has(inp.name)"
                class="form-input flex items-center gap-2 opacity-70 cursor-not-allowed select-none"
              >
                <MdiLock class="w-3.5 h-3.5 text-[var(--color-muted)] flex-shrink-0" />
                <span class="flex-1 truncate text-[var(--color-text)]">
                  {{ isEntityType(inp.type) ? (entityDisplayLabel(inp) || `#${fieldValues[inp.name]}`) : fieldValues[inp.name] }}
                </span>
                <span class="text-[10px] text-[var(--color-muted)] uppercase tracking-wide flex-shrink-0">{{ $t('macros.autoLabel') }}</span>
              </div>

              <!-- text -->
              <BaseInput
                v-else-if="inp.type === 'text'"
                v-model="fieldValues[inp.name]"
                type="text"
                :placeholder="inp.label || inp.name"
              />

              <!-- integer -->
              <BaseInput
                v-else-if="inp.type === 'integer'"
                v-model.number="fieldValues[inp.name]"
                type="number"
                step="1"
                :placeholder="inp.label || inp.name"
              />

              <!-- date -->
              <BaseInput
                v-else-if="inp.type === 'date'"
                v-model="fieldValues[inp.name]"
                type="date"
              />

              <!-- entity searchable dropdown -->
              <div v-else-if="isEntityType(inp.type)" class="relative">
                <button
                  type="button"
                  class="form-input flex items-center justify-between text-left w-full"
                  @click="toggleDropdown(inp, $event)"
                >
                  <span :class="fieldValues[inp.name] ? 'text-[var(--color-text)]' : 'text-[var(--color-muted)]'">
                    {{ entityDisplayLabel(inp) || $t('macros.selectInput', { name: inp.label || inp.type }) }}
                  </span>
                  <svg class="w-4 h-4 text-[var(--color-muted)] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <div
                  v-if="entityDropdownOpen[inp.name]"
                  :class="[
                    'absolute z-50 left-0 right-0 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-xl overflow-hidden',
                    entityDropdownPlacement[inp.name] === 'up' ? 'bottom-[calc(100%+4px)]' : 'top-[calc(100%+4px)]'
                  ]"
                >
                  <div class="p-2 border-b border-[var(--color-border)]">
                    <BaseInput
                      v-model="entitySearch[inp.name]"
                      density="compact"
                      :placeholder="$t('macros.searchInput', { name: inp.label || inp.type })"
                      @click.stop
                    />
                  </div>
                  <div v-if="entityLoading[inp.type]" class="p-3 text-center text-sm text-[var(--color-muted)]">{{ $t('common.loading') }}</div>
                  <div v-else class="max-h-48 overflow-y-auto">
                    <div v-if="!filteredOptions(inp.type).length" class="p-3 text-center text-sm text-[var(--color-muted)]">{{ $t('common.noResults') }}</div>
                    <button
                      v-for="opt in filteredOptions(inp.type)"
                      :key="opt.id"
                      type="button"
                      :class="[
                        'w-full text-left px-3 py-2 text-sm transition-colors',
                        fieldValues[inp.name] == opt.id
                          ? 'bg-[var(--color-primary)] text-white'
                          : 'text-[var(--color-text)] hover:bg-white/5'
                      ]"
                      @click="selectEntity(inp, opt.id)"
                    >{{ opt.label }}</button>
                  </div>
                </div>
              </div>
            </BaseFormField>
          </template>

          <div class="flex gap-2 justify-end pt-1">
            <button class="btn ghost" @click="close">{{ $t('common.cancel') }}</button>
            <button
              class="btn primary"
              :disabled="!allFilled || running"
              @click="submit"
            >
              <svg v-if="running" class="btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <MdiPlay v-else class="w-4 h-4" />
              {{ running ? $t('macros.starting') : $t('macros.run') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
